import { f0FormField, type F0FormRef, useF0FormDefinition } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'
import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'

import { getParticipantsLabel } from 'modules/engagement_surveys/lib/participantStrategyLabels'
import { type MutableRefObject, memo, useMemo } from 'react'

const RECURRENCE_VALUES = ['onetime', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'] as const

const STRATEGY_VALUES = [
  'everyone',
  'all_employees',
  'employees',
  'managers',
  'teams',
  'locations',
  'legal_entities',
] as const

type UseSurveySettingsSchemaParams = {
  disablePublishingFields?: boolean
  disableEndsAtField?: boolean
}

const useSurveySettingsSchema = ({
  disablePublishingFields = false,
  disableEndsAtField = false,
}: UseSurveySettingsSchemaParams) => {
  const multiLocationSelectorFormField = f0FormField(z.array(z.string()).min(1), {
    label: i18n.t('surveys.configuration.participants.locations_label'),
    helpText: i18n.t('surveys.configuration.participants.locations_help_text'),
    section: 'participants',
    fieldType: 'select',
    customFieldName: 'location-selector',
    multiple: true,
    showSearchBox: true,
    renderIf: {
      fieldId: 'participantStrategy',
      equalsTo: 'locations',
    },
  })

  const multiTeamSelectorFormField = f0FormField(z.array(z.string()).min(1), {
    label: i18n.t('surveys.configuration.participants.teams_label'),
    helpText: i18n.t('surveys.configuration.participants.teams_help_text'),
    section: 'participants',
    fieldType: 'select',
    customFieldName: 'team-selector',
    multiple: true,
    showSearchBox: true,
    renderIf: {
      fieldId: 'participantStrategy',
      equalsTo: 'teams',
    },
  })

  const multiLegalEntitySelectorFormField = f0FormField(z.array(z.string()).min(1), {
    label: i18n.t('surveys.configuration.participants.legal_entities_label'),
    helpText: i18n.t('surveys.configuration.participants.legal_entities_help_text'),
    section: 'participants',
    fieldType: 'select',
    customFieldName: 'legal-entity-selector',
    multiple: true,
    showSearchBox: true,
    renderIf: {
      fieldId: 'participantStrategy',
      equalsTo: 'legal_entities',
    },
  })

  const editorIdsFormField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('surveys.configuration.editors.label'),
        section: 'editors',
        mandatory: true,
      }),
    []
  )

  const participantsFormField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('surveys.configuration.participants.employees_label'),
        helpText: i18n.t('surveys.configuration.participants.employees_help_text'),
        section: 'participants',
        mandatory: true,
        renderIf: {
          fieldId: 'participantStrategy',
          equalsTo: 'employees',
        },
      }),
    []
  )

  return z
    .object({
      title: f0FormField(z.string().min(1), {
        label: i18n.t('surveys.configuration.basic_info.name'),
        section: 'basic-info',
      }),
      description: f0FormField(z.string().optional(), {
        label: i18n.t('surveys.configuration.basic_info.description'),
        section: 'basic-info',
        fieldType: 'textarea',
        rows: 3,
      }),
      participantStrategy: f0FormField(z.enum(STRATEGY_VALUES), {
        label: i18n.t('surveys.configuration.participants.label'),
        section: 'participants',
        options: STRATEGY_VALUES.map((value) => ({
          value,
          label: getParticipantsLabel(value),
        })),
      }),
      participants: participantsFormField,
      legalEntities: multiLegalEntitySelectorFormField,
      locations: multiLocationSelectorFormField,
      teams: multiTeamSelectorFormField,
      startDate: f0FormField(z.date().optional(), {
        label: i18n.t('forms.schedule.start_date'),
        helpText: i18n.t('surveys.configuration.publish_settings.start_date_help_text'),
        fieldType: 'datetime',
        section: 'schedule',
        disabled: disablePublishingFields,
        minDate: !disablePublishingFields ? () => new Date() : undefined,
        maxDate: ({ values }: { values: Record<string, unknown> }) =>
          !disablePublishingFields && values ? new Date(`${values.endsAt}`) : undefined,
        renderIf: ({ values }: { values: Record<string, unknown> }) =>
          disablePublishingFields ? !!values.startDate : true,
      }),
      endsAt: f0FormField(z.date().optional(), {
        label: i18n.t('forms.schedule.end_date'),
        fieldType: 'datetime',
        section: 'schedule',
        minDate: ({ values }: { values: Record<string, unknown> }) => {
          if (disableEndsAtField) return undefined

          if (values.startDate) return new Date(`${values.startDate}`)

          return new Date()
        },
        disabled: disableEndsAtField,
        renderIf: ({ values }: { values: Record<string, unknown> }) =>
          disableEndsAtField ? !!values.endsAt : true,
      }),
      recurrenceType: f0FormField(z.enum(RECURRENCE_VALUES), {
        label: i18n.t('forms.schedule.recurrence'),
        section: 'schedule',
        options: [
          { value: 'onetime', label: i18n.t('forms.schedule.recurrence_options.onetime') },
          { value: 'daily', label: i18n.t('forms.schedule.recurrence_options.daily') },
          { value: 'weekly', label: i18n.t('forms.schedule.recurrence_options.weekly') },
          { value: 'monthly', label: i18n.t('forms.schedule.recurrence_options.monthly') },
          { value: 'quarterly', label: i18n.t('forms.schedule.recurrence_options.quarterly') },
          { value: 'yearly', label: i18n.t('forms.schedule.recurrence_options.yearly') },
        ],
        placeholder: i18n.t('forms.schedule.recurrence_options.onetime'),
        disabled: disablePublishingFields,
      }),
      daysToAnswer: f0FormField(z.number().optional(), {
        label: i18n.t('forms.schedule.days_to_answer'),
        section: 'schedule',
        fieldType: 'number',
        renderIf: {
          fieldId: 'recurrenceType',
          notIncludes: 'onetime',
        },
        disabled: disablePublishingFields,
      }),
      restrictedViewersEnabled: f0FormField(z.boolean(), {
        label: i18n.t('surveys.configuration.visibility.restricted_viewers.label'),
        helpText: i18n.t('surveys.configuration.visibility.restricted_viewers.description'),
        section: 'visibility',
        fieldType: 'switch',
      }),
      anonymous: f0FormField(z.boolean(), {
        label: i18n.t('surveys.configuration.privacy_settings.anonymous_answers'),
        helpText: i18n.t('surveys.configuration.privacy_settings.anonymous_answers_description'),
        section: 'visibility',
        fieldType: 'switch',
      }),
      editorIds: editorIdsFormField,
    })
    .superRefine((data, ctx) => {
      if (!disablePublishingFields && data.startDate && data.startDate.getTime() < Date.now()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t(
            'surveys.configuration.publish_settings.start_date_must_be_in_the_future'
          ),
          path: ['startDate'],
        })
      }

      // Validate end date is on or after start date
      if (data.startDate && data.endsAt && data.endsAt < data.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('forms.schedule.end_date_must_be_after_start_date'),
          path: ['endsAt'],
        })
      }

      if (data.startDate && data.endsAt && data.startDate > data.endsAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('forms.schedule.start_date_must_be_before_end_date'),
          path: ['startDate'],
        })
      }
    })
}

export type SurveySettingsFormValues = z.infer<ReturnType<typeof useSurveySettingsSchema>>

const styling = {
  showSectionsSidepanel: false,
}

type SurveySettingsFormProps = {
  defaultValues: Partial<SurveySettingsFormValues>
  onSubmit: (
    values: SurveySettingsFormValues
  ) => Promise<{ success: boolean; rootMessage?: string }>
  disablePublishingFields?: boolean
  disableEndsAtField?: boolean
  formRef?: MutableRefObject<F0FormRef | null>
}

const SurveySettingsFormComponent = ({
  disablePublishingFields = false,
  disableEndsAtField = false,
  defaultValues,
  onSubmit,
  formRef,
}: SurveySettingsFormProps) => {
  const surveySettingsSchema = useSurveySettingsSchema({
    disablePublishingFields,
    disableEndsAtField,
  })

  const submitConfig = useMemo(
    () => ({
      type: 'action-bar' as const,
      label: i18n.t('common.save'),
      discardable: true,
      centerActionBarInFrameContent: true,
    }),
    []
  )

  const sections = useMemo(
    () => ({
      'basic-info': {
        title: i18n.t('surveys.configuration.basic_info.title'),
      },
      participants: {
        title: i18n.t('surveys.configuration.participants.title'),
      },
      schedule: {
        title: i18n.t('surveys.configuration.publish_settings.title'),
      },
      visibility: {
        title: i18n.t('surveys.configuration.visibility.title'),
      },
      editors: {
        title: i18n.t('surveys.configuration.editors.title'),
      },
    }),
    []
  )

  const formDefinition = useF0FormDefinition({
    name: 'survey-settings',
    schema: surveySettingsSchema,
    sections,
    defaultValues,
    submitConfig,
    onSubmit: ({ data }) => onSubmit(data),
  })

  return <FactorialF0Form formDefinition={formDefinition} styling={styling} formRef={formRef} />
}

export const SurveySettingsForm = memo(SurveySettingsFormComponent)
